<template>
  <base-drawer :show="show" title="Copy dashboard to other groups" @close="onClose">
    <drawer-section title="Target groups" icon="icon-single-copy-04">
      <info-hint variant="primary" style="width: 100%">
        Copy
        <span class="text-primary">{{ dashboardName }}</span>
        into other company groups. Each copy includes every widget and its layout. Group-specific
        filters such as a selected <strong>event</strong>, <strong>staff member</strong>, or
        <strong>custom field</strong>, and the global filter bar's staff default, are reset, since
        those do not exist in the other groups. Access settings carry over: the selected roles, and the
        selected users who also belong to the target group.
      </info-hint>

      <div v-if="eligibleGroups.length === 0" class="copy-dashboard-empty">
        There are no other groups to copy this dashboard into.
      </div>

      <div v-else class="copy-dashboard-groups">
        <checkbox
          class="copy-dashboard-select-all"
          :indeterminate="isIndeterminate"
          :value="allSelected"
          @change="toggleAll"
        >
          Select all
        </checkbox>

        <checkbox-group v-model="selectedIds" class="copy-dashboard-list">
          <checkbox
            v-for="group in eligibleGroups"
            :key="group.id"
            class="copy-dashboard-item"
            :label="group.id"
          >
            {{ group.name }}
          </checkbox>
        </checkbox-group>
      </div>
    </drawer-section>

    <template #footer>
      <div class="footer-actions">
        <base-button type="secondary" size="sm" @click="onClose">Cancel</base-button>
        <base-button
          type="primary"
          size="sm"
          :disabled="selectedIds.length === 0 || loading"
          @click="onCopy"
        >
          {{ loading ? 'Copying...' : copyLabel }}
        </base-button>
      </div>
    </template>
  </base-drawer>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from 'vue'
// Element UI's Checkbox isn't globally registered. Import unaliased — the
// babel-plugin-component on-demand resolver derives the lib path from the local
// binding name, so the names MUST stay Checkbox / CheckboxGroup (an alias like
// ElCheckbox would make it look for element-ui/lib/el-checkbox, which 404s).
// Used in template as <checkbox> / <checkbox-group>.
import { Checkbox, CheckboxGroup } from 'element-ui'
import InfoHint from '@/components/InfoHint.vue'

interface Group {
  id: number
  name: string
}

const props = defineProps<{
  show: boolean
  dashboard?: { id?: number; name?: string }
  groups: Group[]
  // Source group id — excluded from the target list (can't copy onto itself).
  sourceGroupId?: number | null
  loading?: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'copy', groupIds: number[]): void
}>()

const selectedIds = ref<number[]>([])

const dashboardName = computed(() => props.dashboard?.name || 'this dashboard')

const eligibleGroups = computed<Group[]>(() =>
  (props.groups || []).filter((g) => Number(g.id) !== Number(props.sourceGroupId))
)

const allSelected = computed(
  () => eligibleGroups.value.length > 0 && selectedIds.value.length === eligibleGroups.value.length
)

const isIndeterminate = computed(
  () => selectedIds.value.length > 0 && !allSelected.value
)

const copyLabel = computed(() => {
  const n = selectedIds.value.length
  if (n <= 1) return 'Copy'
  return `Copy to ${n} groups`
})

function toggleAll(checked: boolean) {
  selectedIds.value = checked ? eligibleGroups.value.map((g) => g.id) : []
}

function onClose() {
  emit('close')
}

function onCopy() {
  if (selectedIds.value.length === 0) return
  emit('copy', [...selectedIds.value])
}

function onEscKey(e: KeyboardEvent) {
  if (e.key === 'Escape' && props.show) onClose()
}

watch(
  () => props.show,
  (visible) => {
    if (visible) {
      selectedIds.value = []
      document.addEventListener('keydown', onEscKey)
    } else {
      document.removeEventListener('keydown', onEscKey)
    }
  }
)

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEscKey)
})
</script>

<style scoped>
/* Theme-neutral gray reads on both dark and light backgrounds. */
.copy-dashboard-empty {
  font-size: 0.875rem;
  color: rgba(125, 125, 125, 0.9);
  margin-top: 0.5rem;
}

.copy-dashboard-groups {
  margin-top: 0.75rem;
}

.copy-dashboard-select-all {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(125, 125, 125, 0.25);
}

.copy-dashboard-list {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  margin-top: 0.85rem;
}

.copy-dashboard-groups ::v-deep .el-checkbox {
  display: flex;
  align-items: center;
  margin-left: 0;
  margin-right: 0;
}

.copy-dashboard-list ::v-deep .el-checkbox + .el-checkbox {
  margin-left: 0;
}

.copy-dashboard-groups ::v-deep .el-checkbox__label {
  white-space: normal;
}

/* Selected state uses the brand primary, not Element UI's default blue. */
.copy-dashboard-groups ::v-deep .el-checkbox__input.is-checked .el-checkbox__inner,
.copy-dashboard-groups ::v-deep .el-checkbox__input.is-indeterminate .el-checkbox__inner {
  background-color: #e14eca;
  border-color: #e14eca;
}

.copy-dashboard-groups ::v-deep .el-checkbox__input.is-checked + .el-checkbox__label {
  color: #e14eca;
}

.copy-dashboard-groups ::v-deep .el-checkbox__inner:hover,
.copy-dashboard-groups ::v-deep .el-checkbox__input.is-focus .el-checkbox__inner {
  border-color: #e14eca;
}

.footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
</style>
